import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Maximize2, ArrowRight } from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import Lightbox, { type LightboxItem } from '@shared/components/Lightbox'

interface EventItem {
    title: string
    date?: string
    image?: string
    contain?: boolean
}

export const EVENTS: EventItem[] = [
    { title: 'Webinar — “Chinese Aggression: Options for India”', date: 'July 2020', image: '/uploads/events/webinar-chinese-aggression-poster.webp', contain: true },
    { title: 'Webinar — Keynote by Maj. Gen. G. D. Bakshi (Retd.)', date: 'July 2020', image: '/uploads/events/webinar-gd-bakshi.webp' },
    { title: 'Webinar — Address by Lt. Gen. Gurmit Singh (Retd.)', date: 'July 2020', image: '/uploads/events/webinar-gurmit-singh.webp' },
    { title: 'Webinar — Participants & Panel', date: 'July 2020', image: '/uploads/events/webinar-participants.webp' },
    { title: 'National Seminar — Inauguration (Lamp Lighting)', date: 'October 2019', image: '/uploads/events/national-seminar-inauguration.webp' },
    { title: 'National Seminar — Plenary Session', date: 'October 2019', image: '/uploads/events/national-seminar-plenary.webp' }
]

function Placeholder() {
    return (
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
            <span
                className="text-[11px] uppercase font-semibold"
                style={{ letterSpacing: '0.16em', color: 'var(--color-rule-strong)' }}
            >
                Event photo
            </span>
        </div>
    )
}

function ViewOverlay() {
    return (
        <span
            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: 'rgba(13,11,8,0.32)' }}
        >
            <span
                className="inline-flex items-center gap-2 px-3.5 py-2 text-[10.5px] uppercase font-semibold"
                style={{
                    letterSpacing: '0.16em',
                    background: 'rgba(243,236,217,0.95)',
                    color: 'var(--color-ink-700)',
                    borderRadius: '2px'
                }}
            >
                <Maximize2 size={13} /> View
            </span>
        </span>
    )
}

function FeatureTile({ item, onOpen }: { item: EventItem; onOpen: () => void }) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="grid md:grid-cols-2 overflow-hidden group"
            style={{
                border: '1px solid var(--color-rule-strong)',
                background: 'var(--color-paper-50)',
                boxShadow: '0 22px 56px -30px rgba(13,27,42,0.22)'
            }}
        >
            <button
                type="button"
                onClick={onOpen}
                className="relative w-full block cursor-zoom-in"
                style={{ minHeight: '20rem', aspectRatio: '4 / 3', background: 'var(--color-paper-100)' }}
                aria-label={`View “${item.title}”`}
            >
                <Placeholder />
                {item.image && (
                    <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className={`relative w-full h-full transition-transform duration-500 group-hover:scale-[1.03] ${item.contain ? 'object-contain p-3' : 'object-cover'}`}
                        onError={(e) => {
                            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                    />
                )}
                <ViewOverlay />
            </button>
            <figcaption className="flex flex-col justify-center p-8 md:p-10">
                <div
                    className="text-[10.5px] uppercase font-semibold"
                    style={{ letterSpacing: '0.22em', color: 'var(--color-gold-700)' }}
                >
                    Featured event
                </div>
                <h3
                    className="font-display mt-4"
                    style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.12, color: 'var(--color-ink-700)', letterSpacing: '-0.015em' }}
                >
                    {item.title}
                </h3>
                {item.date && (
                    <div
                        className="mt-4 text-[11px] uppercase font-semibold"
                        style={{ letterSpacing: '0.2em', color: 'var(--color-ink-400)' }}
                    >
                        {item.date}
                    </div>
                )}
                <p className="mt-5 text-[14.5px] leading-relaxed" style={{ color: 'var(--color-ink-500)', maxWidth: '32rem' }}>
                    A flagship session convened by the Centre, bringing scholars, practitioners and
                    policymakers into open dialogue. Click the image to view the full gallery.
                </p>
                <button
                    type="button"
                    onClick={onOpen}
                    className="link-arrow mt-6 inline-flex self-start"
                >
                    Open in viewer →
                </button>
            </figcaption>
        </motion.figure>
    )
}

function Tile({
    item,
    index,
    onOpen
}: {
    item: EventItem
    index: number
    onOpen: () => void
}) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: (index % 3) * 0.06 }}
            className="overflow-hidden group"
            style={{ border: '1px solid var(--color-rule)', background: 'var(--color-paper-50)' }}
        >
            <button
                type="button"
                onClick={onOpen}
                className="relative w-full block cursor-zoom-in"
                style={{ aspectRatio: '4 / 3', background: 'var(--color-paper-100)' }}
                aria-label={`View “${item.title}”`}
            >
                <Placeholder />
                {item.image && (
                    <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className={`relative w-full h-full transition-transform duration-500 group-hover:scale-[1.03] ${item.contain ? 'object-contain p-2' : 'object-cover'}`}
                        onError={(e) => {
                            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                    />
                )}
                <ViewOverlay />
            </button>
            <figcaption className="px-4 py-4">
                <div className="font-display text-[15px] leading-snug" style={{ color: 'var(--color-ink-700)' }}>
                    {item.title}
                </div>
                {item.date && (
                    <div
                        className="mt-2 text-[10.5px] uppercase font-semibold"
                        style={{ letterSpacing: '0.18em', color: 'var(--color-gold-700)' }}
                    >
                        {item.date}
                    </div>
                )}
            </figcaption>
        </motion.figure>
    )
}

export default function EventsCollaboration() {
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    const lightboxItems: LightboxItem[] = EVENTS.filter((e) => e.image).map((e) => ({
        kind: 'image',
        src: e.image as string,
        caption: e.title
    }))

    const lightboxIndexOf = (galleryIndex: number) => {
        const src = EVENTS[galleryIndex].image
        return lightboxItems.findIndex((it) => it.src === src)
    }

    const openTile = (galleryIndex: number) => {
        const li = lightboxIndexOf(galleryIndex)
        if (li >= 0) setOpenIndex(li)
    }

    const featured = EVENTS[0]
    const rest = EVENTS.slice(1)

    return (
        <>
            <SEO
                title="Events & Collaboration"
                description="Seminars, conferences, webinars and academic collaborations hosted by the Centre for Study of Politics and Governance."
                path="/events-collaboration"
            />
            <PageHeader
                eyebrow="About the Centre"
                title="Events & Collaboration"
                lede="A visual record of the seminars, conferences, webinars and panel discussions convened by the Centre for Study of Politics and Governance."
            />

            <section className="section">
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                        className="grid lg:grid-cols-12 gap-8 lg:gap-12 pb-12"
                        style={{ borderBottom: '1px solid var(--color-rule-strong)' }}
                    >
                        <div className="lg:col-span-4">
                            <div className="eyebrow">The programme</div>
                            <h2 className="section-title mt-5">
                                A forum for serious <em>conversation</em>
                            </h2>
                        </div>
                        <div className="lg:col-span-8">
                            <p className="lede" style={{ maxWidth: '44rem' }}>
                                The Centre convenes seminars, conferences and webinars that bring leading
                                scholars, practitioners and policymakers into rigorous public dialogue on
                                politics, governance and strategic affairs.
                            </p>
                            <p className="prose-body mt-5" style={{ maxWidth: '44rem' }}>
                                The gallery below documents recent sessions; click any image to open it in
                                the in-page reader with zoom, navigation and download.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="section pt-0">
                <div className="container-prose">
                    <FeatureTile item={featured} onOpen={() => openTile(0)} />
                </div>
            </section>

            <section
                className="section paper-grain"
                style={{
                    borderTop: '1px solid var(--color-rule)',
                    borderBottom: '1px solid var(--color-rule)'
                }}
            >
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mb-10"
                    >
                        <div className="kicker">From the archive</div>
                        <h2 className="section-title mt-5">More from our sessions</h2>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {rest.map((e, i) => {
                            const galleryIndex = i + 1
                            return (
                                <Tile
                                    key={e.title}
                                    item={e}
                                    index={i}
                                    onOpen={() => openTile(galleryIndex)}
                                />
                            )
                        })}
                    </div>
                </div>
            </section>

            <section className="section">
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                        className="grid md:grid-cols-12 gap-8 items-center p-8 md:p-11"
                        style={{
                            background: 'var(--color-gold-800)',
                            color: 'var(--color-paper-50)',
                            borderTop: '2px solid var(--color-gold-500)'
                        }}
                    >
                        <div className="md:col-span-8">
                            <div
                                className="text-[10.5px] uppercase font-semibold"
                                style={{ letterSpacing: '0.24em', color: 'var(--color-gold-300)' }}
                            >
                                Collaborate with us
                            </div>
                            <h2
                                className="font-display mt-4"
                                style={{
                                    fontSize: 'clamp(1.7rem, 3.5vw, 2.5rem)',
                                    lineHeight: 1.08,
                                    color: 'var(--color-paper-50)',
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                Co-host your next <em style={{ color: 'var(--color-gold-300)' }}>seminar</em> with the Centre
                            </h2>
                            <p className="mt-4 text-[14.5px] leading-relaxed" style={{ color: 'rgba(243,236,217,0.72)', maxWidth: '38rem' }}>
                                We partner with institutions and scholars to convene conferences, panels and
                                joint research. Tell us what you have in mind and we will explore it together.
                            </p>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Link
                                to="/call-for-academic-collaborations"
                                className="btn btn-gold inline-flex items-center gap-2"
                            >
                                Explore collaborations <ArrowRight size={16} />
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Lightbox
                items={lightboxItems}
                openIndex={openIndex}
                onClose={() => setOpenIndex(null)}
                onIndexChange={setOpenIndex}
            />
        </>
    )
}
